/* eslint-disable */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import IconDefinition from "./IconDefinition";
// CUSTOM HOOKS
import { useLastLiftMessage, useDirectionForLift } from "../socketMessageState/BankMessagesContext";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import { BankInterface, BankFloorInterface } from "../../../interfaces/Bank.interface";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { ElevatorService } from "../../../flatbuffers/generated/service/elevator-service";
// UTILS
import { bitmaskToDoorIconFiltered } from "../../../utils/bitmaskToIcon";
import { bitmaskPrefix } from "../../../utils/typedKeys";
import { stringToUuidT } from "../../../utils/uuid";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";

function IconDefinitionElevatorDefault({
  floor,
  lift,
  bank,
  floorIndex,
  frontDoors,
  rearDoors,
}: {
  floor: BankFloorInterface;
  lift: LiftInterface;
  bank: BankInterface;
  floorIndex: number;
  frontDoors: boolean;
  rearDoors: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [vipMode, setVipMode] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const theme = useSelector((state: RootState) => state.home.theme);
  const doorUnknownAsOpening = useSelector(
    (state: RootState) => state.monitoring.doorUnknownAsOpening,
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const liftUuid = useMemo(() => stringToUuidT(lift.id), [lift.id]);
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const hasFaults: boolean = socketMessageLift.faults;

  const prefix = useMemo(
    () => bitmaskPrefix(socketMessageLift.bankId, socketMessageLift.liftId),
    [socketMessageLift.bankId, socketMessageLift.liftId],
  );

  const liftHasMixedDoors = frontDoors && rearDoors;

  const doorIcon = useMemo(() => {
    const icon = bitmaskToDoorIconFiltered(
      socketMessageLift.bitmask,
      prefix,
      frontDoors,
      rearDoors,
      doorUnknownAsOpening,
      liftHasMixedDoors,
    );
    return icon;
  }, [
    socketMessageLift.bitmask,
    socketMessageLift.position,
    prefix,
    frontDoors,
    rearDoors,
    doorUnknownAsOpening,
    liftHasMixedDoors,
  ]);
  const doorStatusIcon = useMemo(() => {
    const frontOpen = !!socketMessageLift.doorStatus?.frontOpen;
    const rearOpen = !!socketMessageLift.doorStatus?.rearOpen;

    if (liftHasMixedDoors) {
      if (frontDoors && !rearDoors) {
        return frontOpen ? "elFrontOpenRearClose" : "elFrontCloseRearClose";
      }

      if (!frontDoors && rearDoors) {
        return rearOpen ? "elFrontCloseRearOpen" : "elFrontCloseRearClose";
      }

      if (frontDoors && rearDoors) {
        if (frontOpen && rearOpen) return "elFrontOpenRearOpen";
        if (frontOpen && !rearOpen) return "elFrontOpenRearClose";
        if (!frontOpen && rearOpen) return "elFrontCloseRearOpen";
        return "elFrontCloseRearClose";
      }
    }

    if (frontDoors) return frontOpen ? "elOpen" : "elClose";
    if (rearDoors) return rearOpen ? "elRearOpen" : "elRearClose";

    return null;
  }, [socketMessageLift.doorStatus, frontDoors, rearDoors, liftHasMixedDoors]);

  const resolvedDoorIcon = doorIcon || doorStatusIcon;
  const { directionIcon, lastDirectionIcon } = useDirectionForLift(liftUuid);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // USE MEMO *************************************** */
  const activeFloors = useMemo(() => {
    if (!socketMessageLift) {
      return {
        car: new Set<number>(),
        hallUp: new Set<number>(),
        hallDown: new Set<number>(),
        locks: new Set<number>(),
      };
    }

    const car = new Set([
      ...(socketMessageLift.carCalls?.front ?? []),
      ...(socketMessageLift.carCalls?.rear ?? []),
    ]);

    const hallUp = new Set([
      ...(socketMessageLift.hallCalls?.upAssigned?.front ?? []),
      ...(socketMessageLift.hallCalls?.upAssigned?.rear ?? []),
    ]);

    const hallDown = new Set([
      ...(socketMessageLift.hallCalls?.downAssigned?.front ?? []),
      ...(socketMessageLift.hallCalls?.downAssigned?.rear ?? []),
    ]);

    const locks = new Set([
      ...(socketMessageLift.carCalls?.frontLock ?? []),
      ...(socketMessageLift.carCalls?.rearLock ?? []),
      ...(socketMessageLift.hallCalls?.upAssigned?.frontLock ?? []),
      ...(socketMessageLift.hallCalls?.upAssigned?.rearLock ?? []),
      ...(socketMessageLift.hallCalls?.downAssigned?.frontLock ?? []),
      ...(socketMessageLift.hallCalls?.downAssigned?.rearLock ?? []),
    ]);

    return { car, hallUp, hallDown, locks };
  }, [socketMessageLift]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getIcon = () => {
    if (!socketMessageLift) return null;

    if (floor.floor_index === socketMessageLift.position) {
      if (socketMessageLift.position < 0) {
        return (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType="ELEVATOR"
            icon="elWarning"
            iconSize={16}
          />
        );
      }
      const displayDirection = directionIcon || lastDirectionIcon;
      const directionDimmed = !directionIcon && !!lastDirectionIcon;

      // If no door icon and no direction, render nothing
      if (!resolvedDoorIcon && !displayDirection) {
        return null;
      }

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {resolvedDoorIcon && (
            <IconDefinition
              isMap={false}
              expanded={false}
              liftType="ELEVATOR"
              icon={resolvedDoorIcon}
              iconSize={20}
            />
          )}
          {displayDirection && (
            <span style={{ opacity: directionDimmed ? 0.3 : 1 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType="ELEVATOR"
                icon={displayDirection}
                iconSize={20}
              />
            </span>
          )}
        </div>
      );
    }

    if (activeFloors.locks.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elLocked"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallDown.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalRed"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallUp.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalGreen"
          iconSize={20}
        />
      );
    }

    if (activeFloors.car.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoal"
          iconSize={20}
        />
      );
    }

    return null;
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (!socketMessageLift) return;

    setVipMode(
      socketMessageLift.serviceMode?.messageType === DeviceServiceContent.ElevatorServiceMessage &&
        socketMessageLift.serviceMode?.message?.message === ElevatorService.VIP,
    );
  }, [socketMessageLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const containerClass =
    `monitoring__card__liftContainer__${theme} ` +
    (hasFaults
      ? "monitoringView__dangerElevator "
      : vipMode
      ? "monitoringView__vipElevator "
      : "") +
    (floorIndex === 0
      ? hasFaults
        ? "monitoringView__dangerElevator__top "
        : vipMode
        ? "monitoringView__vipElevator__top "
        : ""
      : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? hasFaults
        ? "monitoringView__dangerElevator__bottom "
        : vipMode
        ? "monitoringView__vipElevator__bottom "
        : ""
      : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? " monitoring__card__liftContainerBorderBottom  "
      : "") +
    (floorIndex === 0 ? "monitoring__card__liftContainerBorderTop" : "");

  return <div className={containerClass}>{getIcon()}</div>;
}

export default React.memo(IconDefinitionElevatorDefault);
